import { useMemo, useState } from 'react'

import { Box, Skeleton, Text } from '@factorialco/deprecated-design-system'
import { AvailableModules, Features } from 'generated/featureDefinitions'
import { FileSigned } from '@factorialco/f0-react/icons/app'

import Upgrade from 'modules/core/components/Upgrade'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import FormFieldEditModal from 'modules/expenses/components/FormFieldEditModal'
import { missingFeatureRequestReason } from 'modules/finance/lib/missingFeatureRequestReason'
import { UpsellingKit } from 'modules/upselling_kit/components'
import FormFieldsList from 'modules/expenses/components/FormFieldsList'
import {
  CustomizationField,
  fieldsFor,
  useExpensesCustomization,
} from 'modules/expenses/hooks/useExpensesCustomization'
import SectionInfo from 'modules/expenses/pages/settings/ExpensePolicy/Components/_SectionInfo'

interface FormTypeSectionProps {
  title: string
  description: string
  fields: CustomizationField[]
  isLoading?: boolean
  onClickItemSettings: (field: CustomizationField) => void
}

const FormTypeSection = ({
  title,
  description,
  fields,
  isLoading = false,
  onClickItemSettings,
}: FormTypeSectionProps) => {
  if (isLoading) {
    return (
      <Box flexDirection="column" gap="s16">
        <Box flexDirection="column" gap="s4">
          <Skeleton width="s96" height="s24" isLoading />
          <Skeleton width="full" height="s16" isLoading />
        </Box>
        <Skeleton width="full" height="s64" isLoading />
      </Box>
    )
  }

  return (
    <Box flexDirection="column" gap="s16">
      <Box flexDirection="column" gap="s4">
        <Text.Normal weight="semibold" size="300">
          {title}
        </Text.Normal>
        <Text.Normal size="200" color="grey600">
          {description}
        </Text.Normal>
      </Box>
      <FormFieldsList fields={fields} onClickItemSettings={onClickItemSettings} />
    </Box>
  )
}

interface Props {
  hasPerDiemsFeature: boolean
  hasExpensesFormCustomizationFeature: boolean
}

const FormsRequirements = ({ hasPerDiemsFeature, hasExpensesFormCustomizationFeature }: Props) => {
  const { fields, loading } = useExpensesCustomization()
  const [selectedField, setSelectedField] = useState<CustomizationField | null>(null)
  const [showUpgradeModalFlow, setShowUpgradeModalFlow] = useState(false)
  const { company } = useCurrentSession()

  const expenseFields = useMemo(() => fieldsFor(fields || [], 'expense'), [fields])
  const mileageFields = useMemo(() => fieldsFor(fields || [], 'mileage'), [fields])
  const perdiemFields = useMemo(
    () => (hasPerDiemsFeature ? fieldsFor(fields || [], 'perdiem') : []),
    [fields, hasPerDiemsFeature]
  )

  const onClickItemSettings = (field: CustomizationField) => {
    track(trackingEvents.expenses.goToExpensesFormFieldEdit, {
      expense_type: field.resourceType,
      field_name: field.identifier,
    })

    setSelectedField(field)
  }

  const onCloseModal = () => {
    setSelectedField(null)
  }

  return (
    <Box flexDirection="column" gap="s24" paddingX="s24" flexGrow>
      <SectionInfo icon={FileSigned} title={i18n.t('expenses_v2.settings.forms.title')} />

      {!hasExpensesFormCustomizationFeature && (
        <Upgrade.Small
          title={i18n.t('expenses_v2.settings.forms.upgrade')}
          action={{
            type: 'know_more',
            onClick: () => {
              setShowUpgradeModalFlow(true)
            },
          }}
        />
      )}

      <Box flexDirection="column" gap="s32">
        <FormTypeSection
          title={i18n.t('expenses_v2.settings.forms.types.expense')}
          description={i18n.t('expenses_v2.settings.forms.fields_customization.description', {
            type: i18n.t('expenses_v2.settings.forms.types.expense'),
          })}
          fields={expenseFields}
          isLoading={loading}
          onClickItemSettings={onClickItemSettings}
        />
        <FormTypeSection
          title={i18n.t('expenses_v2.settings.forms.types.mileage')}
          description={i18n.t('expenses_v2.settings.forms.fields_customization.description', {
            type: i18n.t('expenses_v2.settings.forms.types.mileage'),
          })}
          fields={mileageFields}
          isLoading={loading}
          onClickItemSettings={onClickItemSettings}
        />
        {hasPerDiemsFeature && (
          <FormTypeSection
            title={i18n.t('expenses_v2.settings.forms.types.per_diem')}
            description={i18n.t('expenses_v2.settings.forms.fields_customization.description', {
              type: i18n.t('expenses_v2.settings.forms.types.per_diem'),
            })}
            fields={perdiemFields}
            isLoading={loading}
            onClickItemSettings={onClickItemSettings}
          />
        )}
      </Box>

      {selectedField && (
        <FormFieldEditModal field={selectedField} allFields={fields || []} onClose={onCloseModal} />
      )}

      {showUpgradeModalFlow && (
        <UpsellingKit.ProductModal
          isOpen={showUpgradeModalFlow}
          onClose={() => setShowUpgradeModalFlow(false)}
          requestSource="Expenses settings form"
          featureRequested={Features.expenses_form_customization}
          moduleRequested={AvailableModules.expenses}
          requestReason={missingFeatureRequestReason(company)}
          pageSource="expenses_settings_form_customization"
        />
      )}
    </Box>
  )
}

export default FormsRequirements
